import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {
  AddTriggerConfigurationRequest,
  TriggerConfiguration
} from '@models/adminstration/machine/trigger/trigger-configuration';
import {TriggerStylesService} from '@services/styles/administration/machine/trigger/trigger-styles.service';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {TriggerConfigurationService} from '@services/administration/machine/trigger/trigger-configuration.service';
import {TranslateService} from '@ngx-translate/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {takeUntil} from 'rxjs/operators';
import {ReplaySubject} from 'rxjs';
import {TRIGGER_TYPE} from '@constants/administration/machine/trigger-type';
import {TRIGGER_SIGNAL_TYPE} from '@constants/administration/machine/trigger-signal-label';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {
  MachineGroupTriggerConfigurationService
} from '@services/administration/machine-group/trigger/machine-group-trigger-configuration.service';
import {TriggerHelperService} from '@services/trigger/helper/trigger-helper.service';
import {UtilsService} from '@services/utils/utils.service';
import {Machine} from '@models/machine/machine';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {NotificationService} from '@services/notification/notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {ExpressionSignal} from '@models/adminstration/machine/trigger/expression-signal';
import {GatewayService} from '@services/administration/gateway/gateway.service';

@Component({
    selector: 'scitis-machine-group-trigger-configuration-edit',
    templateUrl: './machine-group-trigger-configuration-edit.component.html',
    styleUrls: ['./machine-group-trigger-configuration-edit.component.scss'],
    standalone: false
})
export class MachineGroupTriggerConfigurationEditComponent implements OnInit, OnDestroy {

  @Input()
  public triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();

  @Input()
  public machineGroupId: number | null = null;

  @Input()
  public userRole = new Role(new User());
  public whiteListMachinesIds: number[] | null = null;
  public triggerConfigurationForm: UntypedFormGroup = new UntypedFormGroup({});
  public isLoadingTriggerQueries: boolean = true;
  public isLoadingCloudPlugs: boolean = false;
  public isAdminOrHigher: boolean = false;
  public isDistributorOrHigher: boolean = false;
  public isSuperAdmin: boolean = false;
  public availableTriggerQueries: TriggerQuery[] = [];
  public triggerQueriesList: TriggerQuery[] = [];
  public triggerTypes: string[] = [TRIGGER_TYPE.EVENT_SEVERITY_BASED, TRIGGER_TYPE.VALUE_BASED];
  public severityLabels: string[] = Object.keys(TRIGGER_SIGNAL_TYPE);
  public signals: ExpressionSignal[] = [];
  public cloudPlugs: Gateway[] = [];
  public submitted: boolean = false;
  public valueBasedFormIsValid = false;
  public eventSeverityBasedFormIsValid = false;

  private destroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private COMPONENT_NAME = 'MachineGroupTriggerConfigurationEditComponent';

  public get f(): { [p: string]: AbstractControl } {
    return this.triggerConfigurationForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? !this.formsAreValid() : this.submitted && !this.formsAreValid();
  };

  constructor(
      private triggerStyleService: TriggerStylesService,
      private triggerConfigurationService: TriggerConfigurationService,
      private machineGroupTriggerConfigurationService: MachineGroupTriggerConfigurationService,
      private translateService: TranslateService,
      private gatewayService: GatewayService,
      private activeModal: NgbActiveModal,
      private fb: UntypedFormBuilder,
      private triggerHelper: TriggerHelperService,
      private utilsService: UtilsService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private notificationService: NotificationService
  ) {
  }

  @Input()
  set _machines(value: Machine[]) {
    this.whiteListMachinesIds = value?.map(({id}) => id as number) || null;
  }

  ngOnInit(): void {
    this.isSuperAdmin = this.userRole.isSuperAdminRole;
    this.isAdminOrHigher = this.utilsService.isAdminOrHigher(this.userRole.isAdminRole, this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole);
    this.isDistributorOrHigher = this.isAdminOrHigher || this.userRole.isDistributorRole;
    this.updateResolveFlags();
    this.initForm();
    this.setAvailableTriggerQueries();
    this.onTriggerTypeChange(this.triggerConfiguration.triggerType);
    if (this.triggerConfiguration?.cloudplugId) {
      this.getSelectedGateway(this.triggerConfiguration.cloudplugId);
      this.onLoadSignalsForCloudPlug(this.triggerConfiguration.cloudplugId);
    }
  }

  public onSave(): void {
    if (this.triggerConfigurationForm.invalid) {
      this.submitted = true;
      return;
    }

    let request: AddTriggerConfigurationRequest;

    switch (this.f.triggerType.value) {
      case TRIGGER_TYPE.EVENT_SEVERITY_BASED:
        request = this.triggerHelper.setEventSeverityRequest(
            this.triggerConfiguration,
            this.f.triggerType.value,
            this.f.severity.value,
            this.f.name.value
        );
        break;
      case TRIGGER_TYPE.VALUE_BASED:
        request = this.triggerHelper.setValueBasedRequest(this.triggerConfiguration, this.f.triggerType.value, this.f.name.value);
        break;
      default:
        return;
    }

    this.activeModal.close(request);
  }

  public onClose(): void {
    this.activeModal.dismiss();
  }

  public getStatusCssClass(statusSuccess: boolean): string {
    return this.triggerStyleService.getTriggerConfigurationStatusClass(statusSuccess);
  }

  public onResolveEmailCheckBoxChange($event, triggerQuery: TriggerQuery): void {
    this.triggerConfigurationService.resolveEmailCheckBoxChange(triggerQuery, this.triggerConfiguration);
  }

  public getTriggerQueryInfoStyle(initQueryStarted: boolean): string {
    return this.triggerStyleService.getTriggerConfigurationVisibilityClass(initQueryStarted);
  }

  public showValueBasedComponent() {
    return this.triggerConfigurationService.showValueBasedComponent(this.f.triggerType.value);
  }

  public showEventSeverityBased() {
    return this.triggerConfigurationService.showEventSeverityBased(this.f.triggerType.value);
  }

  public onValueBasedFormIsValid(valueBasedFormIsValid: boolean) {
    this.valueBasedFormIsValid = valueBasedFormIsValid;
  }

  public onUpdatedValueBased(updatedTriggerConfiguration: TriggerConfiguration) {
    this.triggerConfiguration.cloudplugId = updatedTriggerConfiguration.cloudplugId;
    this.triggerConfiguration.signalEdge = updatedTriggerConfiguration.signalEdge;
    this.triggerConfiguration.timeDependencyConfig = updatedTriggerConfiguration.timeDependencyConfig;
    if (!this.triggerConfiguration.signalEdge || this.triggerConfiguration.signalEdge == 'BOTH') {
      this.triggerConfiguration.resolveActions = [];
      console.log('reset');
    }
  }

  onLoadSignalsForCloudPlug(gatewayId: string): void {
    this.triggerConfiguration.cloudplugId = gatewayId;
    if (this.triggerConfigurationService.isSignalBasedTrigger(this.f.triggerType.value)) {
      this.triggerConfigurationService.getSignalsLoadingSubject().next(true);
      this.triggerConfigurationService.getTriggerExpressionSignals(gatewayId).subscribe(
          {
            next: (expressionSignals: ExpressionSignal[]) => {
              if (expressionSignals?.length > 0) {
                expressionSignals = expressionSignals.filter(m => !m.triggerSignal);
                this.signals = this.tableHeaderSortableService.sortItems<ExpressionSignal>(expressionSignals, 'signalLabel', 'asc');
                this.triggerConfigurationService.setExpressionsLabelsAndUnits(this.triggerConfiguration.triggerExpressions, this.signals);
              } else {
                this.signals = [];
              }
              this.triggerConfigurationService.getSignalsLoadingSubject().next(false);
            }, error: error => {
              this.notificationService.error('TriggerConfigModal. Error getting signal list.', error, this.COMPONENT_NAME, true);
              this.triggerConfigurationService.getSignalsLoadingSubject().next(false);
            }
          });
    }
  }

  public getSelectedGateway(gatewayId: string): void {
    this.isLoadingCloudPlugs = true;
    this.gatewayService.getGatewayDetails(gatewayId).pipe(takeUntil(this.destroy)).subscribe(
        {
          next: (data) => {
            if (data) {
              this.cloudPlugs = [data];
            }
            this.isLoadingCloudPlugs = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting gateway.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.isLoadingCloudPlugs = false;
          }
        });
  }

  public onRemoveTriggerQuery(selectedTriggerQuery: TriggerQuery): void {
    this.triggerConfiguration.triggerQueries = this.triggerConfiguration.triggerQueries.filter(triggerQuery => triggerQuery.id !== selectedTriggerQuery.id);
    this.triggerQueriesList.push(selectedTriggerQuery);
    this.triggerQueriesList = [...this.triggerQueriesList];
  }

  public onTriggerActionSelected(selectedTriggerQuery: TriggerQuery): void {
    if (!this.triggerConfiguration.triggerQueries) {
      this.triggerConfiguration.triggerQueries = [];
    }
    this.triggerConfiguration.triggerQueries.push(selectedTriggerQuery);
    this.triggerQueriesList = this.triggerQueriesList.filter(triggerQuery => triggerQuery.id !== selectedTriggerQuery.id);
    this.f.triggerAction.setValue('');
  }

  public onTriggerTypeChange(triggerType: TRIGGER_TYPE): void {
    this.setEventSeverityControl(triggerType === TRIGGER_TYPE.EVENT_SEVERITY_BASED);
    this.valueBasedFormIsValid = true;
    this.eventSeverityBasedFormIsValid = true;
    switch (triggerType) {
      case TRIGGER_TYPE.VALUE_BASED:
        this.valueBasedFormIsValid = false;
        break;
      case TRIGGER_TYPE.EVENT_SEVERITY_BASED:
        this.eventSeverityBasedFormIsValid = false;
        break;
    }
  }

  public removeTriggerExpressions() {
    this.triggerConfiguration.triggerExpressions = [];
  }

  public formsAreValid(): boolean {
    return this.triggerConfigurationForm?.valid && this.nestedFormIsValid();
  }

  private nestedFormIsValid(): boolean {
    if (this.userRole.isDistributorRole) {
      return true;
    }
    switch (this.f.triggerType.value) {
      case TRIGGER_TYPE.VALUE_BASED:
        return this.valueBasedFormIsValid;
      case TRIGGER_TYPE.EVENT_SEVERITY_BASED:
        return this.f.severity?.valid;
    }
    return false;
  }

  private setAvailableTriggerQueries(): void {
    if (this.machineGroupId) {
      this.isLoadingTriggerQueries = true;
      this.machineGroupTriggerConfigurationService.machineGroupAvailableTriggerQueries(this.machineGroupId)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (result: TriggerQuery[]) => {
          this.availableTriggerQueries = result;
          this.triggerQueriesList = result;
          this.isLoadingTriggerQueries = false;
        }, error: error => {
          console.log(error);
          this.isLoadingTriggerQueries = false;
          this.notificationService.error('Error loading trigger queries', error, this.COMPONENT_NAME, true);
        }
      });
    }
  }

  private initForm(): void {
    this.triggerConfigurationForm = this.fb.group({
      name: new UntypedFormControl({
        value: this.triggerConfiguration.name,
        disabled: !this.isAdminOrHigher
      }, [Validators.required]),
      triggerType: new UntypedFormControl({
        value: this.triggerConfiguration.triggerType,
        disabled: !this.isAdminOrHigher
      }, [Validators.required]),
      triggerAction: new UntypedFormControl('')
    });
    this.setEventSeverityControl(this.triggerConfiguration.triggerType === TRIGGER_TYPE.EVENT_SEVERITY_BASED);
  }

  private setEventSeverityControl(isAddControl: boolean = true) {
    if (isAddControl) {
      const selectedSeverities: string[] = [];
      for (const expression of this.triggerConfiguration.triggerExpressions) {
        if (expression?.triggerLastValue) {
          selectedSeverities.push(expression.triggerLastValue);
        }
      }


      this.triggerConfigurationForm.addControl('severity', new UntypedFormControl({
        value: this.triggerHelper.getEventLabelByArray(selectedSeverities) ?? [TRIGGER_SIGNAL_TYPE.ERROR],
        disabled: !this.isAdminOrHigher
      }, [Validators.required]));
    } else {
      this.triggerConfigurationForm.removeControl('severity');
    }
    this.triggerConfigurationForm.updateValueAndValidity();
  }

  private updateResolveFlags() {
    if (this.triggerConfiguration.triggerQueries && this.triggerConfiguration.resolveActions?.length > 0) {
      this.triggerConfiguration.triggerQueries.forEach(triggerQuery => {
        if (!triggerQuery.hasOwnProperty('isResolveEvent')) {
          triggerQuery.isResolveEvent = false;
        }
        if (this.triggerConfiguration.resolveActions.indexOf(triggerQuery.id) > -1) {
          triggerQuery.isResolveEvent = true;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
